import { useEffect, useState } from "react";
import api from "../../api";

// A read-only reference page (the brand bible, Who buys DSL) fetched through
// the API so it stays behind the login, then shown in a sandboxed frame. The
// pages carry their own styles, so a frame keeps them from leaking into the
// cockpit. `allow-scripts` without `allow-same-origin` gives the frame an
// opaque origin: the page's own charts run, but it can't read the session.
// A srcDoc frame resolves "#section" links against the cockpit's own URL, so a
// table-of-contents click loads the whole app inside the frame, where the
// opaque origin has no session and it shows the sign-in screen. This script,
// appended to the page, scrolls to the section instead of navigating.
const ANCHOR_FIX = `<script>
document.addEventListener("click", function (e) {
  var a = e.target.closest && e.target.closest('a[href^="#"]');
  if (!a) return;
  e.preventDefault();
  var id = decodeURIComponent(a.getAttribute("href").slice(1));
  var el = id && (document.getElementById(id) || document.getElementsByName(id)[0]);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  else if (!id) window.scrollTo({ top: 0, behavior: "smooth" });
});
</script>`;

export default function InfoPage({ slug, title }) {
  const [html, setHtml] = useState(null);
  const [error, setError] = useState(null);
  const [full, setFull] = useState(false);

  useEffect(() => {
    let live = true;
    setHtml(null);
    setError(null);
    api.get(`/marketing/docs/${slug}`)
      .then(({ data }) => live && setHtml(data.html))
      .catch((e) => live && setError(e?.response?.data?.detail || "Couldn't load this page"));
    return () => { live = false; };
  }, [slug]);

  useEffect(() => {
    if (!full) return undefined;
    const onKey = (e) => e.key === "Escape" && setFull(false);
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [full]);

  // A new tab gets the page as a blob. The page is ours (served from the repo),
  // so running it outside the sandbox is acceptable; the URL is revoked once
  // the tab has had time to load it.
  const openTab = () => {
    const url = URL.createObjectURL(new Blob([html], { type: "text/html" }));
    window.open(url, "_blank", "noopener");
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  };

  if (error) return <div className="mk-info-empty" data-testid={`mk-info-${slug}-error`}>{error}</div>;
  if (html === null) return <div className="mk-info-empty">Loading…</div>;
  return (
    <div className={`mk-info ${full ? "full" : ""}`}>
      <div className="mk-info-bar">
        <span className="mk-info-title">{title}</span>
        <button type="button" className="mk-info-btn" onClick={() => setFull(!full)}
          data-testid={`mk-info-${slug}-full`}>
          {full ? "Close full screen ✕" : "Full screen ⤢"}
        </button>
        <button type="button" className="mk-info-btn" onClick={openTab}
          data-testid={`mk-info-${slug}-tab`}>
          New tab ↗
        </button>
      </div>
      <iframe
        className="mk-info-frame"
        title={title}
        srcDoc={html + ANCHOR_FIX}
        sandbox="allow-scripts allow-popups"
        data-testid={`mk-info-${slug}`}
      />
    </div>
  );
}
